import React, { useCallback, useContext, useMemo } from 'react';
import { PurchaseOrder } from '../../graphql/entities';
import attributeQuery from '../../graphql/queries/purchases/PurchaseAttributeConfigListQuery.graphql';
import supplierAttributeQuery from '../../graphql/queries/supplier/SupplierAttributeConfigListQuery.graphql';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import Formatters from '../../components/grid-addons/formatters';
import { useAttributesForWorkspace } from '../../components/Workspace/useAttributesForWorkspace';
import Workspace from '../../components/Workspace';
import { CostViewPermission, Direction, IPurchaseOrder } from '../../graphql/schema/p4types';
import { Paths } from '../../@types/keyOfNested';
import { ModalFormatter } from '../../components/grid-addons/formatters/ModalFormatter';
import { IFormatterProps } from '../../@types/workspaceTypes';
import { useId } from '@fluentui/react-hooks';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { BasicHeader } from '../../actions/BasicHeader';
import { DirectionalHint } from '@fluentui/react';
import { StyledPill } from '../../components/StyledPill/StyledPill';
import { useFullUserDetails } from '../../utils/hooks';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import {
  IWorkspaceActionButtonsProps,
  WorkspaceActionButtons,
} from '../../components/Workspace/WorkspaceActionButtons';
import { usePurchaseOrderWorkspaceActions } from '../../actions/purchasing/PurchaseOrderWorkspaceAction';

const LazySupplierForm = React.lazy(() => import('../../views/supplier/SupplierForm'));
const { formUrl, type, title, workspace } = PurchaseOrder;

const DeliveryReferencesFormatter = (formatterProps: IFormatterProps<IPurchaseOrder>) => {
  const tooltipId = useId(`deliveryReferences${formatterProps.rowIdx}`);
  const deliveryReferences = formatterProps.dependentValues.rowData?.deliveryReferences;
  if (deliveryReferences) {
    const deliveryReferencesArray = deliveryReferences.split('|');
    const numberOfDeliveries = deliveryReferencesArray.length;
    return (
      <TooltipHost
        content={
          <>
            <BasicHeader>Delivery References</BasicHeader>
            <div style={{ whiteSpace: 'pre-line', padding: '1em' }}>
              {deliveryReferencesArray.map((reference) => {
                if (!reference) return '';
                return `${reference} \r\n`;
              })}
            </div>
          </>
        }
        // This id is used on the tooltip itself, not the parent
        id={tooltipId}
        directionalHint={DirectionalHint.leftCenter}
        calloutProps={{ styles: { root: { padding: 0 } } }}
      >
        <StyledPill>
          {numberOfDeliveries} {numberOfDeliveries > 1 ? 'References' : 'Reference'}
        </StyledPill>
      </TooltipHost>
    );
  }
  return null;
};

const PurchaseOrderWorkspaceActionButtons = (props: IWorkspaceActionButtonsProps) => {
  const [purchaseOrderWorkspaceActions, modals] = usePurchaseOrderWorkspaceActions();
  return (
    <>
      {modals}
      <WorkspaceActionButtons {...props} actionButtons={purchaseOrderWorkspaceActions} />
    </>
  );
};

const PurchaseOrderWorkspace = (props) => {
  const fullUserDetails = useFullUserDetails();

  const fieldNames: Array<Paths<IPurchaseOrder>> = useMemo(() => {
    const passedFields = props.fieldNames ? props.fieldNames : [];
    const headerTotal =
      fullUserDetails?.companyUser?.costViewPermission === CostViewPermission.All ? ['headerTotal'] : [];
    if (props.fieldNameOverrides) {
      return props.fieldNameOverrides;
    }
    return [
      'orderNumber',
      'createDate',
      'supplier.reference',
      'supplier.name',
      'orderReference',
      'status',
      ...headerTotal,
      ...passedFields,
    ];
  }, [fullUserDetails?.companyUser?.costViewPermission, props.fieldNameOverrides, props.fieldNames]);

  const columnOverrides = useMemo(
    () => ({
      orderNumber: {
        name: 'Order No',
        key: 'orderNumber',
        width: 100,
      },
      'supplier.reference': {
        name: 'Supplier Ref',
        formatter: ModalFormatter,
        width: 125,
        options: {
          modalContent: LazySupplierForm,
          modalProps: { forceReadOnly: true },
          entityId: 'supplier.id',
        },
      },
      'supplier.name': {
        name: 'Supplier Name',
      },
      headerTotal: {
        name: 'Total',
        cellClass: 'rightAlign',
      },
      status: {
        name: 'Status',
        sortable: false,
      },
      targetDate: {
        name: 'Expected Delivery Date',
      },
      grossGoodsTotal: {
        name: 'Gross Goods Excl VAT',
      },
      packagingVolumeTotal: {
        formatter: Formatters.NumericFormatter,
        options: { format: { decimals: 9 }, suppressTrailingZeros: true, suppressZeroes: true },
      },
      'currency.description': {
        name: 'Currency',
      },
      carriageCostCalculationMethod: {
        name: 'Carriage Calculation Method',
      },
      'supplier.address.country.description': {
        name: 'Supplier Address - Country',
      },
      'deliveryAddress.country.description': {
        name: 'Delivery Address - Country',
      },
      baseVatTotal: {
        name: 'Base VAT Total',
      },
      vatTotal: {
        name: 'VAT Total',
      },
      carriageTotalExVat: {
        name: 'Carriage Total Excl VAT',
      },
      'vatBusinessCategory.description': {
        name: 'VAT Business Category - Description',
      },
      deliveryReferences: {
        name: 'Delivery References',
        sortable: false,
        formatter: DeliveryReferencesFormatter,
      },
      ...(props.columnOverrides ? props.columnOverrides : []),
    }),
    [props.columnOverrides]
  );

  const supplierAttributeColumns = useAttributesForWorkspace(supplierAttributeQuery, {
    valuesPath: 'supplier.attributeValues',
    columnKey: 'supplier.attributeValues.attributeConfig.title',
  });
  const purchaseAttributeColumns = useAttributesForWorkspace(attributeQuery);

  const customColumns = useMemo(
    () => ({ ...purchaseAttributeColumns, ...supplierAttributeColumns }),
    [purchaseAttributeColumns, supplierAttributeColumns]
  );

  const workspaceProps = useWorkspace<IPurchaseOrder>(props, PurchaseOrder, {
    columns: fieldNames,
    customColumns: customColumns,
    columnOverrides: columnOverrides,
    initialSortColumn: 'orderNumber',
    initialSortDirection: Direction.Desc,
    additionalQueryOptions: props.additionalQueryOptions,
  });

  const availableColumnsFilter = useCallback(
    (column: IEditableDataGridColumnDef): boolean => {
      if (!column?.fieldName) {
        return false;
      }
      return !(
        fullUserDetails?.companyUser?.costViewPermission !== CostViewPermission.All &&
        (column.fieldName === 'headerTotal' ||
          column.fieldName === 'nettGoodsTotal' ||
          column.fieldName === 'vatTotal' ||
          column.fieldName === 'grossGoodsTotal' ||
          column.fieldName === 'baseHeaderTotal' ||
          column.fieldName === 'baseNettGoodsTotal' ||
          column.fieldName === 'baseVatTotal' ||
          column.fieldName === 'baseGrossGoodsTotal')
      );
    },
    [fullUserDetails?.companyUser?.costViewPermission]
  );

  return (
    <Workspace
      ref={props.setWorkspaceRef}
      entityType={type}
      formUrl={formUrl}
      title={title}
      defaultQuery={workspace}
      duplicate={true}
      availableColumnsFilter={availableColumnsFilter}
      {...workspaceProps}
      {...props}
      entity={PurchaseOrder}
      workspaceActionButtonsHOC={PurchaseOrderWorkspaceActionButtons}
    />
  );
};

export default PurchaseOrderWorkspace;
